import React, { useEffect, useState } from 'react';
import { listInvoices, deleteInvoice } from '@/pages/billing/services/billingInvoices.services';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const Currency = ({ value }) => <span>${(Number(value || 0)).toLocaleString('es-CO')}</span>;

export default function FacturacionPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const generateInvoicePDF = (inv) => {
    if (!inv) return;
    const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
    const itemsRows = (inv.items || []).map(it => `
      <tr>
        <td>${it.description}</td>
        <td style="text-align:right">${it.quantity}</td>
        <td style="text-align:right">${fmt(it.unitPrice)}</td>
        <td style="text-align:right">${fmt(it.amount)}</td>
      </tr>
    `).join('');
    const recRows = ((inv.details?.receptionsByLaundry) || []).map(r => {
      const unit = 5;
      const minRate = 100000;
      const kilos = Number(r.kilos || 0);
      const base = kilos * unit;
      // Overrides se guardan por ID en la estructura actual
      const ov = inv.details?.receptionOverrides?.[r.lavanderiaId] || {};
      // Nombre: usar campo directo, o el mapa helper, o fallback al ID
      const name = r.nombre || inv.details?.laundryNameMap?.[r.lavanderiaId] || r.lavanderiaId;
      const fixed = Number(ov?.fixedAmount || 0);
      const sub = base;
      const disc = ov?.fixedEnabled ? Math.max(0, base - fixed) : Number(ov?.discount || 0);
      const total = ov?.fixedEnabled ? fixed : Math.max(0, base - disc);
      return `
        <tr>
          <td>${name}</td>
          <td style="text-align:right">${kilos}</td>
          <td style="text-align:right">${fmt(unit)}</td>
          <td style="text-align:right">${fmt(minRate)}</td>
          <td style="text-align:right">${fmt(sub)}</td>
          <td style="text-align:right">${fmt(disc)}</td>
          <td style="text-align:right">${fmt(total)}</td>
        </tr>
      `;
    }).join('');
    const workedRows = (inv.details?.workedUsers || []).map(u => {
      const excluded = !!u.excluded;
      const disc = excluded ? 5000 : 0;
      const total = excluded ? 0 : 5000;
      return `
        <tr>
          <td>${u.nombre || ''}</td>
          <td>${u.cedula || '-'}</td>
          <td>${u.rol || '-'}</td>
          <td style="text-align:right">${u.count || 0}</td>
          <td style="text-align:right">${fmt(disc)}</td>
          <td style="text-align:right">${fmt(total)}</td>
        </tr>
      `;
    }).join('');
    const adminRows = (inv.details?.adminUsers || []).map(u => `
      <tr><td>${u.nombre || ''}</td><td>${u.correo || '-'}</td></tr>
    `).join('');
    const supRows = (inv.details?.supervisorUsers || []).map(u => `
      <tr><td>${u.nombre || ''}</td><td>${u.correo || '-'}</td></tr>
    `).join('');
    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Factura ${inv?.meta?.reference || ''}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #111; }
            h1, h2 { margin: 0 0 8px; }
            h1 { font-size: 18px; }
            h2 { font-size: 16px; margin-top: 24px; }
            .muted { color: #666; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
            .right { text-align: right; }
          </style>
        </head>
        <body>
          <h1>Liquidación - ${inv?.meta?.reference || ''}</h1>
          <div class="muted">Periodo: ${String(inv.period?.from || '').slice(0, 10)} a ${String(inv.period?.to || '').slice(0, 10)}</div>
          <h2>Resumen</h2>
          <table>
            <thead><tr><th>Concepto</th><th class="right">Cantidad</th><th class="right">Precio Unitario</th><th class="right">Valor</th></tr></thead>
            <tbody>${itemsRows}</tbody>
            <tfoot>
              <tr><td colspan="3"><strong>Subtotal</strong></td><td class="right"><strong>${fmt(inv.subtotal)}</strong></td></tr>
              <tr><td colspan="3">IVA</td><td class="right">${fmt(inv.taxes?.iva || 0)}</td></tr>
              <tr><td colspan="3"><strong>Total</strong></td><td class="right"><strong>${fmt(inv.total)}</strong></td></tr>
            </tfoot>
          </table>
          <h2>Kilos recepcionados (por lavandería)</h2>
          <table>
            <thead><tr><th>Descripción</th><th class="right">Kilos</th><th class="right">V.Unit</th><th class="right">Tarifa mínima</th><th class="right">Subtotal</th><th class="right">Descuento</th><th class="right">Total</th></tr></thead>
            <tbody>${recRows}</tbody>
          </table>
          <h2>Usuarios con registro de horas</h2>
          <table>
            <thead><tr><th>Nombre</th><th>Documento</th><th>Rol</th><th class="right">Fichajes</th><th class="right">Descuento</th><th class="right">Total</th></tr></thead>
            <tbody>${workedRows}</tbody>
          </table>
          <h2>Usuarios administradores</h2>
          <table><thead><tr><th>Nombre</th><th>Correo</th></tr></thead><tbody>${adminRows}</tbody></table>
          <h2>Usuarios supervisores</h2>
          <table><thead><tr><th>Nombre</th><th>Correo</th></tr></thead><tbody>${supRows}</tbody></table>
          <script>window.onload=()=>window.print();</script>
        </body>
      </html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await listInvoices({ page: 1, limit: 20 });
      setItems(res?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = async (id) => {
    await deleteInvoice(id);
    await refresh();
  };

  const statusVariant = {
    Pagada: "success",
    Pendiente: "warning",
    Cancelada: "destructive",
  };

  return (
    <div className="container mx-auto p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Facturación</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando...</div>
          ) : !items.length ? (
            <div className="text-sm text-muted-foreground">No hay facturas generadas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border rounded">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 border">Fecha</th>
                    <th className="text-left p-2 border">Referencia</th>
                    <th className="text-left p-2 border">Periodo</th>
                    <th className="text-right p-2 border">Estado</th>
                    <th className="text-right p-2 border">Subtotal</th>
                    <th className="text-right p-2 border">IVA</th>
                    <th className="text-right p-2 border">Total</th>
                    <th className="text-right p-2 border">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(inv => (
                    <tr key={inv._id}>
                      <td className="p-2 border">{String(inv.createdAt).slice(0, 19).replace('T', ' ')}</td>
                      <td className="p-2 border">{inv?.meta?.reference || '-'}</td>
                      <td className="p-2 border">{String(inv?.period?.from).slice(0, 10)} a {String(inv?.period?.to).slice(0, 10)}</td>
                      <td className="p-2 border text-right text-lg">
                        <Badge variant={statusVariant[inv?.status] || "secondary"}>
                          {inv?.status || "Pendiente"}
                        </Badge>
                      </td>
                      <td className="p-2 border text-right"><Currency value={inv?.subtotal} /></td>
                      <td className="p-2 border text-right"><Currency value={inv?.taxes?.iva || 0} /></td>
                      <td className="p-2 border text-right"><Currency value={inv.total} /></td>
                      <td className="p-2 border text-right flex gap-2 justify-end">
                        <Button className="" variant="secondary" size="sm" title="Descargar PDF" onClick={() => generateInvoicePDF(inv)}>
                          <span style={{ fontSize: '14px' }}>📄</span>
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(inv._id)} disabled={true}>Eliminar</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
